import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Stack,
  Divider,
  Switch,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import { CustomBreadcrumbs, PageBanner, TabMenu, CustomTableContainer, CustomTable, CustomTableHead, CustomTableRow, CustomTableCell, CustomTableBody, ConfirmationModal } from '../../common';
import {
  fetchNotifications,
  clearAllNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  deleteNotification,
  getNotificationPreference,
  toggleNotificationPreference
} from '../../../infrastructure/services/notificationService';
import settingsBannerBg from '../../../assets/images/setting.jpg';

const IOSSwitch = styled((props) => <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />)(
  ({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(16px) !important',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: '#FF502D',
          opacity: 1,
          border: 0,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 22,
      height: 22,
    },
    '& .MuiSwitch-track': {
      borderRadius: 26 / 2,
      backgroundColor: '#cbd5e1',
      opacity: 1,
    },
  })
);

export default function SettingsNotificationsPage() {
  const navigate = useNavigate();
  const [systemNotificationsEnabled, setSystemNotificationsEnabled] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const breadcrumbItems = [
    { label: 'Home', href: '/dashboard' },
    { label: 'Settings', onClick: () => navigate('/settings'), style: { cursor: 'pointer' } },
    { label: 'Notifications' }
  ];

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const loadPreferences = async () => {
    try {
      const pref = await getNotificationPreference();
      if (pref && typeof pref.enabled === 'boolean') {
        setSystemNotificationsEnabled(pref.enabled);
        localStorage.setItem('systemNotificationsEnabled', String(pref.enabled));
      }
    } catch (err) {
      console.error('Failed to load notification preference:', err);
    }
  };

  const loadNotifications = async () => {
    try {
      const data = await fetchNotifications();
      setNotifications(data || []);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      showToast('Failed to load notifications list.', 'error');
    }
  };

  const initData = async () => {
    setIsLoading(true);
    await Promise.all([loadPreferences(), loadNotifications()]);
    setIsLoading(false);
  };

  useEffect(() => {
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleChange = async (checked) => {
    setSystemNotificationsEnabled(checked);
    localStorage.setItem('systemNotificationsEnabled', String(checked));
    window.dispatchEvent(new Event('notificationSettingsChanged'));

    try {
      await toggleNotificationPreference(checked);
      showToast(
        checked ? 'System notifications enabled successfully!' : 'System notifications disabled.',
        'success'
      );
    } catch (err) {
      console.error('Failed to toggle notification preference:', err);
      showToast('Failed to update notification settings preference.', 'error');
      // Revert state
      setSystemNotificationsEnabled(!checked);
      localStorage.setItem('systemNotificationsEnabled', String(!checked));
      window.dispatchEvent(new Event('notificationSettingsChanged'));
    }
  };

  const handleMarkAsRead = async (id) => {
    setIsActionLoading(true);
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
      showToast('Notification marked as read.', 'success');
      // Dispatch custom event to notify page header
      window.dispatchEvent(new Event('notificationSettingsChanged'));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      showToast('Failed to mark notification as read.', 'error');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setIsActionLoading(true);
    try {
      await deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      showToast('Notification deleted successfully.', 'success');
      // Dispatch custom event to notify page header
      window.dispatchEvent(new Event('notificationSettingsChanged'));
    } catch (err) {
      console.error('Failed to delete notification:', err);
      showToast('Failed to delete notification.', 'error');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleClearAll = () => {
    if (notifications.length === 0) return;
    setIsConfirmModalOpen(true);
  };

  const handleConfirmClearAll = async () => {
    setIsConfirmModalOpen(false);
    setIsActionLoading(true);
    try {
      await clearAllNotifications();
      setNotifications([]);
      showToast('All notifications cleared.', 'success');
      // Dispatch custom event to notify page header
      window.dispatchEvent(new Event('notificationSettingsChanged'));
    } catch (err) {
      console.error('Failed to clear notifications:', err);
      showToast('Failed to clear notifications.', 'error');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    setIsActionLoading(true);
    try {
      const unread = notifications.filter(n => !n.isRead);
      if (unread.length > 0) {
        await markAllNotificationsAsRead();
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      }
      showToast('All notifications marked as read.', 'success');
      window.dispatchEvent(new Event('notificationSettingsChanged'));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      showToast('Failed to mark all as read.', 'error');
    } finally {
      setIsActionLoading(false);
    }
  };

  const formatTableTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ pb: 5, fontFamily: 'Poppins, sans-serif' }}>
      {/* Breadcrumbs */}
      <CustomBreadcrumbs items={breadcrumbItems} sx={{ mb: 2 }} />

      {/* Page Banner with background setting.jpg */}
      <PageBanner
        title="Settings"
        description="Manage your notification preferences or review historical updates."
        backgroundImage={settingsBannerBg}
      />

      {/* Reusable Tab Switcher */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
        <TabMenu
          tabs={[
            { id: 'profile', label: 'Profile', icon: <PersonRoundedIcon /> },
            { id: 'notifications', label: 'Notification', icon: <NotificationsRoundedIcon /> }
          ]}
          activeTab="notifications"
          onChange={(tabId) => navigate(tabId === 'profile' ? '/settings' : '/settings/notifications')}
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#FF502D' }} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 3,
            mt: 4,
            alignItems: 'start',
            width: '100%'
          }}
        >
          {/* Card 1: Notifications Table (Moved to Left Side) */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              border: '1px solid #e2e8f0',
              borderRadius: '24px',
              bgcolor: '#ffffff',
              boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '10px',
                    bgcolor: 'rgba(99,102,241,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <NotificationsRoundedIcon sx={{ fontSize: '18px', color: '#6366f1' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '16px', color: '#1e293b', fontFamily: 'Poppins' }}>
                    Notifications History
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'Poppins' }}>
                    List of updates, schedule alerts, and system warnings.
                  </Typography>
                </Box>
              </Box>

              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={notifications.filter((n) => !n.isRead).length === 0 || isActionLoading}
                  onClick={handleMarkAllRead}
                  sx={{
                    borderRadius: '10px',
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#cbd5e1',
                    color: '#475569',
                    '&:hover': {
                      borderColor: '#94a3b8',
                      bgcolor: '#f8fafc'
                    }
                  }}
                >
                  Mark All as Read
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  disabled={notifications.length === 0 || isActionLoading}
                  onClick={handleClearAll}
                  sx={{
                    borderRadius: '10px',
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#fda4af',
                    color: '#e11d48',
                    '&:hover': {
                      borderColor: '#f43f5e',
                      bgcolor: '#fff1f2'
                    }
                  }}
                >
                  Clear All
                </Button>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    pl: 0.5,
                    ml: 0.5,
                    borderLeft: '1px solid #e2e8f0'
                  }}
                >
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#475569', fontFamily: 'Poppins' }}>
                    Enable Notifications
                  </Typography>
                  <IOSSwitch checked={systemNotificationsEnabled} onChange={(e) => handleToggleChange(e.target.checked)} />
                </Box>
              </Stack>
            </Box>
            <Divider sx={{ mb: 3, borderColor: '#f1f5f9' }} />

            <CustomTableContainer sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <CustomTable>
                <CustomTableHead sx={{ bgcolor: '#f8fafc' }}>
                  <CustomTableRow>
                    <CustomTableCell sx={{ fontWeight: 700, fontFamily: 'Poppins', color: '#475569', fontSize: '13px' }}>Notification</CustomTableCell>
                    <CustomTableCell sx={{ fontWeight: 700, fontFamily: 'Poppins', color: '#475569', fontSize: '13px' }}>Received At</CustomTableCell>
                    <CustomTableCell sx={{ fontWeight: 700, fontFamily: 'Poppins', color: '#475569', fontSize: '13px' }}>Status</CustomTableCell>
                    <CustomTableCell align="right" sx={{ fontWeight: 700, fontFamily: 'Poppins', color: '#475569', fontSize: '13px', pr: 3 }}>Actions</CustomTableCell>
                  </CustomTableRow>
                </CustomTableHead>
                <CustomTableBody>
                  {notifications.length === 0 ? (
                    <CustomTableRow>
                      <CustomTableCell colSpan={4} align="center" sx={{ py: 6, color: '#94a3b8', fontFamily: 'Poppins', fontSize: '14px' }}>
                        No notifications found.
                      </CustomTableCell>
                    </CustomTableRow>
                  ) : (
                    notifications.map((item) => (
                      <CustomTableRow key={item.id} hover sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                        <CustomTableCell sx={{ py: 2 }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            {item.image && (
                              <Box
                                component="img"
                                src={item.image}
                                alt={item.title || 'Notification Image'}
                                onClick={() => window.open(item.image, '_blank')}
                                sx={{
                                  width: 50,
                                  height: 50,
                                  borderRadius: '10px',
                                  objectFit: 'cover',
                                  border: '1px solid #e2e8f0',
                                  flexShrink: 0,
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s ease',
                                  '&:hover': {
                                    transform: 'scale(1.08)',
                                  }
                                }}
                              />
                            )}
                            <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '13.5px', fontFamily: 'Poppins' }}>
                                {item.title || 'Notification'}
                              </Typography>
                              <Typography sx={{ color: '#64748b', fontSize: '12.5px', fontFamily: 'Poppins' }}>
                                {item.description || ''}
                              </Typography>
                            </Stack>
                          </Stack>
                        </CustomTableCell>
                        <CustomTableCell sx={{ py: 2, fontFamily: 'Poppins', color: '#475569', fontSize: '13px' }}>
                          {formatTableTime(item.createdAt)}
                        </CustomTableCell>
                        <CustomTableCell sx={{ py: 2 }}>
                          <Chip
                            label={item.isRead ? 'Read' : 'Unread'}
                            size="small"
                            sx={{
                              bgcolor: item.isRead ? '#f1f5f9' : 'rgba(255, 80, 45, 0.08)',
                              color: item.isRead ? '#64748b' : '#FF502D',
                              fontWeight: 700,
                              fontFamily: 'Poppins',
                              borderRadius: '8px',
                              fontSize: '10px'
                            }}
                          />
                        </CustomTableCell>
                        <CustomTableCell align="right" sx={{ py: 2, pr: 3 }}>
                          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ display: 'inline-flex' }}>
                            {!item.isRead && (
                              <Tooltip title="Mark as Read">
                                <IconButton
                                  size="small"
                                  onClick={() => handleMarkAsRead(item.id)}
                                  disabled={isActionLoading}
                                  sx={{ color: '#475569', '&:hover': { color: '#2563eb' } }}
                                >
                                  <MarkEmailReadRoundedIcon sx={{ fontSize: '18px' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(item.id)}
                                disabled={isActionLoading}
                                sx={{ color: '#475569', '&:hover': { color: '#ef4444' } }}
                              >
                                <DeleteRoundedIcon sx={{ fontSize: '18px' }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </CustomTableCell>
                      </CustomTableRow>
                    ))
                  )}
                </CustomTableBody>
              </CustomTable>
            </CustomTableContainer>
          </Paper>
        </Box>
      )}

      {/* Toast notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', fontFamily: 'Poppins', fontSize: '14px', borderRadius: '10px' }}>
          {toast.message}
        </Alert>
      </Snackbar>
      {/* Clear All Confirmation Dialog */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmClearAll}
        title="Clear All Notifications"
        description="Are you sure you want to clear all notifications? This action cannot be undone."
        confirmText="Clear All"
        cancelText="Cancel"
        type="delete"
      />
    </Box>
  );
}
